import React, { forwardRef, useEffect, useMemo, useState } from "react";

import { Icon } from "components/Icon";
import styled from "styled-components";

import {
    INCORPORATED_GROUP_MODIFY_API, INCORPORATED_GROUP_REMOVE_API,
    SMS_SUPPLIER_GAN_GROUP_MODIFY_METHOD, SMS_SUPPLIER_GAN_GROUP_REMOVE_METHOD
} from "const/apiMethods";
import { sortData, servicePick, copyArrayWithoutReference } from "utils";
import { checkPermissionsFor } from "store/storeHelpers";
import { ASC_SORT } from "const";
import { ButtonRow, ButtonToolbar } from "components/base/BaseButton";
import Table from "components/Table";


import { IconButton, Popover, Tooltip } from "rsuite";
import Whisper from "components/Whisper";

export default forwardRef((
    {
        data,
        rowKey,
        service,
        loading,
        loadableGroups,
        paymentTermsList,
        showUploadButton,
        isSupervisorSms,

        onEditClick,
        onRemoveClick,
        onUploadClick,
        onDownloadClick,
        onSelectItems,
        ...props
    }, ref
) => {

    const paymentsColumnWidth = useMemo(() => {
        const billingIncrementWidth = servicePick(service, 55, 0);
        return billingIncrementWidth + (110 * (paymentTermsList.length || 2));
    }, [service, paymentTermsList]);

    const modifyMethod = servicePick(service, INCORPORATED_GROUP_MODIFY_API, SMS_SUPPLIER_GAN_GROUP_MODIFY_METHOD);
    const showModifyButton = checkPermissionsFor(modifyMethod);
    const removeMethod = servicePick(service, INCORPORATED_GROUP_REMOVE_API, SMS_SUPPLIER_GAN_GROUP_REMOVE_METHOD);
    const showRemoveButton = checkPermissionsFor(removeMethod);

    const columns = useMemo(() => {
        return [
            {
                id: "supplier_name",
                dataKey: "supplier_name",
                value: ({ supplier_name, supplier_comment, comment }) => <>
                    <span title={supplier_name}>{supplier_name}</span>
                    {supplier_comment && !comment && <IconComment comment={supplier_comment} />}
                </>,
                label: "Supplier",
                // flexGrow: 1,
                minWidth: 220,
                sortable: true
            },
            {
                id: "name",
                dataKey: "name",
                value: ({ name }) => <>
                    <span title={name}>{name}</span>
                </>,
                label: "Group name",
                // flexGrow: 1,
                minWidth: 200,
                sortable: true
            },
            {
                id: "comment",
                dataKey: "comment",
                value: ({ comment }) => <>
                    <span title={comment}>{comment}</span>
                </>,
                label: "Comment",
                flexGrow: 1,
                minWidth: 300,
                sortable: true
            },
            {
                id: "test_number_list",
                dataKey: "test_number_list",
                label: "Test numbers",
                value: ({ test_number_list }) => {
                    const text = test_number_list && test_number_list.join(", ");
                    return <span title={text}>{text}</span>
                },
                width: 140,
                sortable: true
            },
            {
                id: "numbers",
                dataKey: "all_numbers",
                label: "Numbers",
                value: ({ all_numbers, allocated_numbers }) => `${allocated_numbers}/${all_numbers}`,
                width: 100,
                sortable: true
            },
            {
                id: "rate",
                dataKey: "rate",
                label: "Inc. payout",
                value: ({ rate, cur_name, supplier_cur_name }) => `${rate} ${servicePick(service, cur_name, supplier_cur_name)}`,
                width: 100,
                sortable: true
            },
            {
                id: "rate_list",
                dataKey: "rate_list",
                label: servicePick(service, "Billing increment & Dialer's payouts", "Dialer's payouts"),
                value: ({ rate_list, rrc_name }) => {
                    const payoutGroups = rate_list.reduce((summ, item) => {
                        return {
                            ...summ,
                            [item.cur_name]: summ[item.cur_name]
                                ? summ[item.cur_name] + `, ${item.payment_terms_name}: ${item.rate} ${item.cur_name}`
                                : `${item.payment_terms_name}: ${item.rate} ${item.cur_name}`
                        }
                    }, {});

                    return (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            {service && <div style={{ width: 70, paddingRight: 0, textAlign: "left", flexShrink: 0 }}>{rrc_name}</div>}
                            <div style={{ fontSize: 12, lineHeight: "1.2em", flexShrink: 0, flexGrow: 1 }}>
                                {Object.keys(payoutGroups).map((item) => <span>{payoutGroups[item]}<br /></span>, "")}
                            </div>
                        </div>
                    );
                },
                width: paymentsColumnWidth,

            },
            ...(!service ? [{
                id: "trafic_limit_list",
                label: "Traffic limits",
                dataKey: "trafic_limit_list",
                // sortable: true,
                value: ({ trafic_limit_list }) => {
                    const sortedList = copyArrayWithoutReference(trafic_limit_list).sort((a, b) => {
                        if (a.senderid < b.senderid) {
                            return -1;
                        } else if (a.senderid >= b.senderid) {
                            return 1;
                        }
                        return 0;
                    });

                    const firstElement = sortedList.length ? sortedList[0] : {};
                    const remainingElementsList = sortedList.splice(1, sortedList.length);

                    const stringElementsList = remainingElementsList.map(value => {
                        const hourDayValue = !value?.hour && !value?.day ? null : `${value.hour} hour/${value.day} day`;

                        if (hourDayValue) {
                            return `${value?.senderid || "<empty>"} — ${hourDayValue}`;
                        }

                        return "No limits";
                    });

                    const newLinedString = stringElementsList.length ? stringElementsList.map(element => <p>{element}</p>) : "No more limits";

                    const hour = firstElement.hour;
                    const day = firstElement.day;
                    const limitDay = day ? `${day} SMS per day` : "";
                    const limitHour = hour ? `${hour} SMS per hour` : "";
                    const bothLimitsSeparator = day && hour ? "/" : "";

                    const finalValue = limitDay && limitHour ? <>
                        <>{firstElement.senderid || "Any senderId"}</> — <SecondStagedCell>
                            <span className="first">{limitDay}</span>
                            <span className="second">{limitHour}</span>
                        </SecondStagedCell>
                    </> : "No limits";

                    const hourDayValue = `${limitDay}${bothLimitsSeparator}${limitHour}`;
                    const finalValueTooltip = hourDayValue ? `${firstElement.senderid || "Any senderId"} — ${hourDayValue}${stringElementsList.length ? "..." : ""}` : "No limits";


                    return (
                        <Whisper
                            enterable
                            placement="auto"
                            speaker={
                                // Back later
                                <Tooltip>{hourDayValue && !stringElementsList.length ? finalValueTooltip : newLinedString}</Tooltip>
                            }
                        >
                            <span style={{ fontSize: 13, display: "flex" }}>{finalValue}</span>
                        </Whisper>
                    );
                },
                minWidth: 220,
                minWidthMobile: 140
            }] : []),
            {
                id: "actions",
                dataKey: "actions",
                width: 150,
                value: (rowData) => {
                    const isManagedDisabled = !service ? !rowData?.is_managed : false;

                    return (
                        <ButtonToolbar style={{ marginTop: 0 }}>
                            {showModifyButton && <ButtonRow
                                as={IconButton}
                                icon={<Icon icon="edit" />}
                                disabled={isManagedDisabled}
                                onClick={() => {
                                    const groupData = service ? {
                                        rate_list: rowData.rate_list,
                                        name: rowData.name,
                                        rate: rowData.rate,
                                        ing_key: rowData.ing_key,
                                        rrc_key: rowData.rrc_key,
                                        comment: rowData.comment,
                                        ivr_id: rowData.ivr_id
                                    } : {
                                        rate_list: rowData.rate_list,
                                        comment: rowData.comment,
                                        name: rowData.name,
                                        rate: rowData.rate,
                                        id: rowData.id
                                    };

                                    onEditClick(groupData);
                                }}
                            />}
                            {showUploadButton &&
                                <ButtonRow
                                    as={IconButton}
                                    icon={<Icon icon="plus" />}
                                    disabled={isManagedDisabled}
                                    onClick={() => {
                                        const groupData = service ? {
                                            supplier_key: rowData.supplier_key,
                                            rate_list: rowData.rate_list,
                                            name: rowData.name,
                                            rate: rowData.rate,
                                            ing_key: rowData.ing_key
                                        } : {
                                            supplier_id: rowData.supplier_id,
                                            comment: rowData.comment,
                                            name: rowData.name,
                                            rate: rowData.rate,
                                            id: rowData.id,
                                            supplier_trunk_id: rowData?.trunk?.id
                                        };

                                        onUploadClick(groupData);
                                    }}
                                />
                            }
                            <ButtonRow
                                as={IconButton}
                                icon={<Icon icon="cloud-download" />}
                                // disabled={isManagedDisabled}
                                loading={loadableGroups.includes(rowData[rowKey])}
                                onClick={() => onDownloadClick(rowData[rowKey])}
                            />
                            {showRemoveButton && <ButtonRow
                                as={IconButton}
                                icon={<Icon icon="trash" />}
                                disabled={isManagedDisabled}
                                onClick={onRemoveClick}
                            />}
                        </ButtonToolbar>
                    );
                }
            }
        ]
    }, [loadableGroups, paymentTermsList, showUploadButton, paymentsColumnWidth]);

    const [sortColumn, setSortColumn] = useState("supplier_name");
    const [sortType, setSortType] = useState(ASC_SORT);
    const [dataState, setData] = useState(data !== null ? data : data);

    useEffect(() => {
        if (data !== null) {
            setData(data);
        }
    }, [data]);

    const handleSortColumn = (column, type) => {
        setSortColumn(column);
        setSortType(type);
        setData(sortData(dataState, column, type));
    };

    return (
        <Table
            ref={ref}
            virtualized
            checkboxPositionStart={true}
            shouldUpdateScroll={false}
            data={dataState}
            extraHeight={(dataState.length && dataState.length < 7) ? (dataState.length * 40) + 40 : 280}
            columns={columns}
            loading={loading}
            rowHeight={40}
            setSelected={onSelectItems}
            onSortColumn={handleSortColumn}
            sortType={sortType}
            sortColumn={sortColumn}
            {...props}
        />
    )
});

const IconComment = ({
    comment,
    // group
}) => {

    return (
        <span style={{ marginLeft: 10, display: "inline-block" }}>
            <Whisper
                enterable={true}
                placement="rightStart"
                speaker={<Popover style={{ width: 300 }}>{comment}</Popover>}
            >
                <span
                    className={"rs-icon rs-icon-commenting"}
                    style={{ color: "var(--color-main)" }}
                />
            </Whisper>
        </span>
    );
};

const SecondStagedCell = styled.div`
    && {
        display: flex;
        height: 40px;
        flex-direction: column;
        font-size: 12px;
        margin-top: -4px;
        line-height: 1.2;
        margin-left: 4px;
    }
`;